import { rgba } from 'polished';
import { ENABLE_QUARTZ_COLORS } from '../api';
import { COLOR_TOKENS } from './color_tokens';
import { PRIMITIVE_COLORS, deepFreeze } from './primitive_colors';

export const COLOR_MIGRATIONS = deepFreeze({
    actionsTan: {
        legacy: '#F7F1EC',
        quartz: PRIMITIVE_COLORS.secondary.tan[200],
    },
    alert: {
        legacy: '#fffcea',
        quartz: PRIMITIVE_COLORS.secondary.tan[200],
    },
    badgeBlue: {
        legacy: 'rgba(48, 82, 218, 0.5)',
        quartz: PRIMITIVE_COLORS.accent.cobalt[200],
    },
    badgeBlue15: {
        legacy: 'rgba(48, 82, 218, 0.15)',
        quartz: PRIMITIVE_COLORS.accent.cobalt[50],
    },
    badgeBlue25: {
        legacy: 'rgba(48, 82, 218, 0.25)',
        quartz: PRIMITIVE_COLORS.accent.cobalt[100],
    },
    badgeBrown: {
        legacy: 'rgba(161, 108, 47, 0.5)',
        quartz: PRIMITIVE_COLORS.secondary.tan[600],
    },
    badgeBrown25: {
        legacy: 'rgba(161, 108, 47, 0.25)',
        quartz: PRIMITIVE_COLORS.secondary.tan[500],
    },
    badgeDarkPink: {
        legacy: 'rgba(253, 167, 255, 0.8)',
        quartz: PRIMITIVE_COLORS.semantic.red[200],
    },
    badgeDarkPink25: {
        legacy: 'rgba(253, 167, 255, 0.25)',
        quartz: PRIMITIVE_COLORS.semantic.red[50],
    },
    badgeDarkPink50: {
        legacy: 'rgba(253, 167, 255, 0.5)',
        quartz: PRIMITIVE_COLORS.semantic.red[100],
    },
    badgeGreen: {
        legacy: 'rgba(53, 166, 108, 0.5)',
        quartz: PRIMITIVE_COLORS.semantic.green[200],
    },
    badgeLightBlue: {
        legacy: 'rgba(156, 222, 255, 0.6)',
        quartz: PRIMITIVE_COLORS.primary.aqua[200],
    },
    badgeOrange: {
        legacy: 'rgba(241, 90, 38, 0.5)',
        quartz: PRIMITIVE_COLORS.brand.orange[200],
    },
    badgeOrange25: {
        legacy: 'rgba(241, 90, 38, 0.25)',
        quartz: PRIMITIVE_COLORS.brand.orange[100],
    },
    badgePink: {
        legacy: 'rgba(255, 169, 185, 0.5)',
        quartz: PRIMITIVE_COLORS.semantic.red[50],
    },
    badgePrismGreen25: {
        legacy: 'rgba(14, 98, 7, 0.25)',
        quartz: PRIMITIVE_COLORS.semantic.green[100],
    },
    badgePrismOrange25: {
        legacy: 'rgba(239, 108, 0, 0.25)',
        quartz: PRIMITIVE_COLORS.brand.orange[100],
    },
    badgeViolet: {
        legacy: 'rgba(146, 94, 199, 0.5)',
        quartz: PRIMITIVE_COLORS.accent.purple[200],
    },
    badgeViolet15: {
        legacy: 'rgba(146, 94, 199, 0.15)',
        quartz: PRIMITIVE_COLORS.accent.purple[50],
    },
    badgeViolet25: {
        legacy: 'rgba(146, 94, 199, 0.25)',
        quartz: PRIMITIVE_COLORS.accent.purple[100],
    },
    badgeYellow: {
        legacy: 'rgba(250, 192, 59, 0.5)',
        quartz: PRIMITIVE_COLORS.semantic.yellow[200],
    },
    badgeYellow25: {
        legacy: 'rgba(250, 192, 59, 0.25)',
        quartz: PRIMITIVE_COLORS.semantic.yellow[50],
    },
    bannerPeach: {
        legacy: '#F8C4B3',
        quartz: PRIMITIVE_COLORS.brand.orange[100],
    },
    bannerRed: {
        legacy: '#DA99A4',
        quartz: PRIMITIVE_COLORS.semantic.red[200],
    },
    black: {
        legacy: '#282828',
        quartz: COLOR_TOKENS.text.primary,
    },
    black00: {
        legacy: '#000000',
        quartz: PRIMITIVE_COLORS.neutral.black,
    },
    black2: {
        legacy: '#080808',
        quartz: PRIMITIVE_COLORS.neutral.black,
    },
    black3: {
        legacy: '#3A3A3A',
        quartz: PRIMITIVE_COLORS.neutral.grey[900],
    },
    blue: {
        legacy: '#9CDEFF',
        quartz: PRIMITIVE_COLORS.primary.aqua[500],
    },
    blue1: {
        legacy: '#3350D1',
        quartz: PRIMITIVE_COLORS.accent.cobalt[500],
    },
    blue2: {
        legacy: '#4560D5',
        quartz: PRIMITIVE_COLORS.accent.cobalt[400],
    },
    blue3: {
        legacy: '#2b60e7',
        quartz: PRIMITIVE_COLORS.accent.cobalt[600],
    },
    blue4: {
        legacy: '#6EA9E4',
        quartz: PRIMITIVE_COLORS.semantic.blue[500],
    },
    blue5: {
        legacy: '#114E85',
        quartz: PRIMITIVE_COLORS.semantic.blue[900],
    },
    blue6: {
        legacy: '#68D2EE',
        quartz: PRIMITIVE_COLORS.primary.aqua[600],
    },
    blue8: {
        legacy: '#9FE7F5',
        quartz: PRIMITIVE_COLORS.primary.aqua[500],
    },
    blue9: {
        legacy: '#BFEFF8',
        quartz: PRIMITIVE_COLORS.primary.aqua[300],
    },
    blueHighlight: {
        legacy: '#E1F8FC',
        quartz: PRIMITIVE_COLORS.primary.aqua[100],
    },
    brandBlue2: {
        legacy: '#D0EDFC',
        quartz: PRIMITIVE_COLORS.primary.aqua[200],
    },
    brandBlue3: {
        legacy: '#F1F6FC',
        quartz: PRIMITIVE_COLORS.semantic.blue[50],
    },
    brandGreen2: {
        legacy: '#A2D4BA',
        quartz: PRIMITIVE_COLORS.semantic.green[200],
    },
    brandPeach2: {
        legacy: '#FCD7BF',
        quartz: PRIMITIVE_COLORS.brand.orange[100],
    },
    brandRed2: {
        legacy: '#DBA1AB',
        quartz: PRIMITIVE_COLORS.semantic.red[200],
    },
    brandYellow2: {
        legacy: '#FAE0A4',
        quartz: PRIMITIVE_COLORS.semantic.yellow[200],
    },
    brandYellow3: {
        legacy: '#FDEBC2',
        quartz: PRIMITIVE_COLORS.semantic.yellow[100],
    },
    brandYellow4: {
        legacy: '#695119',
        quartz: PRIMITIVE_COLORS.semantic.yellow[900],
    },
    brown: {
        legacy: '#a16c2f',
        quartz: PRIMITIVE_COLORS.semantic.yellow[800],
    },
    buttonPrimaryGradient: {
        legacy: 'linear-gradient(to top,#1991eb,#2da1f8)',
        quartz: `linear-gradient(to top,${PRIMITIVE_COLORS.accent.cobalt[500]},${PRIMITIVE_COLORS.primary.aqua[700]})`,
    },
    categoryBenefits: {
        legacy: '#E88B00',
        quartz: PRIMITIVE_COLORS.brand.orange[400],
    },
    categoryDiscriminationEqualEmployment: {
        legacy: '#3B9DCE',
        quartz: PRIMITIVE_COLORS.semantic.blue[500],
    },
    categoryHiring: {
        legacy: '#B54124',
        quartz: PRIMITIVE_COLORS.brand.orange[700],
    },
    categoryLeavesAccommodations: {
        legacy: '#925EC7',
        quartz: PRIMITIVE_COLORS.accent.purple[400],
    },
    categoryManagingEmployees: {
        legacy: '#2F7152',
        quartz: PRIMITIVE_COLORS.semantic.green[600],
    },
    categorySafetyHealth: {
        legacy: '#1D1DC2',
        quartz: PRIMITIVE_COLORS.accent.cobalt[700],
    },
    categoryTermination: {
        legacy: '#7D197D',
        quartz: PRIMITIVE_COLORS.accent.purple[600],
    },
    categoryWagesHours: {
        legacy: '#915F43',
        quartz: PRIMITIVE_COLORS.semantic.yellow[900],
    },
    chatAvatarBase: {
        legacy: '#0067D2',
        quartz: PRIMITIVE_COLORS.accent.cobalt[600],
    },
    chatAvatarWhite: {
        legacy: '#FDFDFD',
        quartz: PRIMITIVE_COLORS.neutral.grey[50],
    },
    chatBrandBase: {
        legacy: '#1232AF',
        quartz: PRIMITIVE_COLORS.accent.cobalt[700],
    },
    componentsBlue15: {
        legacy: 'rgba(156, 222, 255, 0.15)',
        quartz: PRIMITIVE_COLORS.primary.aqua[50],
    },
    componentsBox1: {
        legacy: '#F8F1EB',
        quartz: PRIMITIVE_COLORS.secondary.tan[200],
    },
    componentsBox2: {
        legacy: '#F8F8F8',
        quartz: PRIMITIVE_COLORS.neutral.grey[50],
    },
    componentsDisabled: {
        legacy: '#E1E5E9',
        quartz: PRIMITIVE_COLORS.neutral.graphite[100],
    },
    componentsModalOverlayBlack: {
        legacy: 'rgba(100, 119, 133, 0.71)',
        quartz: PRIMITIVE_COLORS.overlay.default.rgba,
    },
    componentsTableTitleRow: {
        legacy: '#F5F5F5',
        quartz: PRIMITIVE_COLORS.neutral.grey[50],
    },
    componentsTan2: {
        legacy: '#F4EEEA',
        quartz: PRIMITIVE_COLORS.secondary.tan[300],
    },
    componentsTan30: {
        legacy: 'rgba(239, 227, 217, 0.3)',
        quartz: PRIMITIVE_COLORS.secondary.tan[100],
    },
    componentsTan50: {
        legacy: 'rgba(239, 227, 217, 0.5)',
        quartz: PRIMITIVE_COLORS.secondary.tan[200],
    },
    componentsTheGreyStyle: {
        legacy: '#F9F9F9',
        quartz: PRIMITIVE_COLORS.neutral.grey[50],
    },
    componentsTheGreyStyle50: {
        legacy: 'rgba(249, 249, 249, 0.5)',
        quartz: PRIMITIVE_COLORS.neutral.grey[50],
    },
    dark: {
        legacy: '#A4A4A4',
        quartz: PRIMITIVE_COLORS.neutral.grey[400],
    },
    darkest: {
        legacy: '#5E5E5E',
        quartz: PRIMITIVE_COLORS.neutral.grey[800],
    },
    darkGrey: {
        legacy: '#535353',
        quartz: PRIMITIVE_COLORS.neutral.grey[800],
    },
    errorRed100: {
        legacy: '#F5C6CB',
        quartz: PRIMITIVE_COLORS.semantic.red[100],
    },
    errorRed500: {
        legacy: '#721C24',
        quartz: PRIMITIVE_COLORS.semantic.red[700],
    },
    favoriteOrange: {
        legacy: '#FF5A1B',
        quartz: PRIMITIVE_COLORS.brand.orange[500],
    },
    green: {
        legacy: '#35A66C',
        quartz: PRIMITIVE_COLORS.semantic.green[400],
    },
    green1: {
        legacy: '#006666',
        quartz: PRIMITIVE_COLORS.semantic.green[700],
    },
    green2: {
        legacy: '#F0FFDF',
        quartz: PRIMITIVE_COLORS.semantic.green[50],
    },
    green3: {
        legacy: '#288054',
        quartz: COLOR_TOKENS.text.success,
    },
    green4: {
        legacy: '#127E09',
        quartz: PRIMITIVE_COLORS.semantic.green[500],
    },
    green5: {
        legacy: '#2E8B57',
        quartz: PRIMITIVE_COLORS.semantic.green[500],
    },
    grey: {
        legacy: '#B9B9B9',
        quartz: PRIMITIVE_COLORS.neutral.grey[300],
    },
    grey11: {
        legacy: 'rgba(40, 40, 40, 0.12)',
        quartz: PRIMITIVE_COLORS.neutral.grey[100],
    },
    grey12: {
        legacy: 'rgba(40, 40, 40, 0.08)',
        quartz: PRIMITIVE_COLORS.neutral.grey[100],
    },
    grey13: {
        legacy: 'rgba(40, 40, 40, 0.2)',
        quartz: PRIMITIVE_COLORS.neutral.grey[200],
    },
    grey14: {
        legacy: '#696969',
        quartz: PRIMITIVE_COLORS.neutral.grey[700],
    },
    grey15: {
        legacy: '#E6EAEE',
        quartz: PRIMITIVE_COLORS.neutral.graphite[100],
    },
    grey16: {
        legacy: '#A9A9A9',
        quartz: PRIMITIVE_COLORS.neutral.grey[400],
    },
    grey17: {
        legacy: 'rgba(0, 0, 0, 0.16)',
        quartz: PRIMITIVE_COLORS.neutral.grey[200],
    },
    grey18: {
        legacy: '#939598',
        quartz: PRIMITIVE_COLORS.neutral.grey[500],
    },
    grey19: {
        legacy: '#848484',
        quartz: PRIMITIVE_COLORS.neutral.grey[600],
    },
    grey2: {
        legacy: '#ABABAB',
        quartz: PRIMITIVE_COLORS.neutral.grey[400],
    },
    grey20: {
        legacy: '#808285',
        quartz: PRIMITIVE_COLORS.neutral.grey[600],
    },
    grey21: {
        legacy: '#E6E7E8',
        quartz: PRIMITIVE_COLORS.neutral.grey[100],
    },
    grey22: {
        legacy: '#BCBEC0',
        quartz: PRIMITIVE_COLORS.neutral.grey[300],
    },
    grey23: {
        legacy: '#58595B',
        quartz: PRIMITIVE_COLORS.neutral.grey[800],
    },
    grey26: {
        legacy: '#0d1d3814',
        quartz: PRIMITIVE_COLORS.neutral.grey[100],
    },
    grey27: {
        legacy: '#6D6E71',
        quartz: PRIMITIVE_COLORS.neutral.grey[700],
    },
    grey28: {
        legacy: '#D9D9D9',
        quartz: PRIMITIVE_COLORS.neutral.grey[200],
    },
    grey29: {
        legacy: '#C4CCD3',
        quartz: PRIMITIVE_COLORS.neutral.graphite[200],
    },
    grey3: {
        legacy: '#999999',
        quartz: PRIMITIVE_COLORS.neutral.grey[500],
    },
    grey30: {
        legacy: '#5B6C79',
        quartz: PRIMITIVE_COLORS.neutral.graphite[600],
    },
    grey31: {
        legacy: '#CDCED0',
        quartz: PRIMITIVE_COLORS.neutral.grey[200],
    },
    grey32: {
        legacy: '#282828',
        quartz: PRIMITIVE_COLORS.neutral.black,
    },
    grey4: {
        legacy: '#666666',
        quartz: PRIMITIVE_COLORS.neutral.grey[700],
    },
    grey5: {
        legacy: '#979797',
        quartz: PRIMITIVE_COLORS.neutral.grey[500],
    },
    grey6: {
        legacy: '#F2F3F6',
        quartz: PRIMITIVE_COLORS.neutral.graphite[50],
    },
    grey61: {
        legacy: 'rgba(40, 40, 40, 0.16)',
        quartz: PRIMITIVE_COLORS.neutral.grey[200],
    },
    grey8: {
        legacy: '#424242',
        quartz: PRIMITIVE_COLORS.neutral.grey[900],
    },
    grey9: {
        legacy: '#E0E4E7',
        quartz: PRIMITIVE_COLORS.neutral.graphite[100],
    },
    greyD4: {
        legacy: '#D4D4D4',
        quartz: PRIMITIVE_COLORS.neutral.grey[200],
    },
    greyPrismDesc: {
        legacy: '#00000099',
        quartz: PRIMITIVE_COLORS.neutral.grey[700],
    },
    handbookFederal: {
        legacy: '#1A91EB',
        quartz: PRIMITIVE_COLORS.semantic.blue[500],
    },
    handbookMultistate: {
        legacy: '#F5A623',
        quartz: PRIMITIVE_COLORS.brand.orange[300],
    },
    handbookOnestate: {
        legacy: '#7ED321',
        quartz: PRIMITIVE_COLORS.semantic.green[400],
    },
    informationBlue100: {
        legacy: '#D2E4F7',
        quartz: PRIMITIVE_COLORS.semantic.blue[100],
    },
    informationBlue200: {
        legacy: '#9EC5ED',
        quartz: PRIMITIVE_COLORS.semantic.blue[300],
    },
    informationBlue300: {
        legacy: '#C6D4F1',
        quartz: PRIMITIVE_COLORS.semantic.blue[200],
    },
    informationBlue500: {
        legacy: '#1A5276',
        quartz: PRIMITIVE_COLORS.semantic.blue[900],
    },
    informationBlue900: {
        legacy: '#2E4760',
        quartz: PRIMITIVE_COLORS.semantic.blue[900],
    },
    inputIconError: {
        legacy: '#216BA5',
        quartz: PRIMITIVE_COLORS.semantic.blue[700],
    },
    inProgressStatus: {
        legacy: '#8A6A20',
        quartz: PRIMITIVE_COLORS.semantic.yellow[800],
    },
    light: {
        legacy: '#C7C7C7',
        quartz: PRIMITIVE_COLORS.neutral.grey[200],
    },
    light1: {
        legacy: '#A7A7A7',
        quartz: PRIMITIVE_COLORS.neutral.grey[400],
    },
    lightBanner: {
        legacy: '#F7F6F1',
        quartz: PRIMITIVE_COLORS.secondary.tan[200],
    },
    lightblue: {
        legacy: '#CEEFFF',
        quartz: PRIMITIVE_COLORS.primary.aqua[200],
    },
    lightblue1: {
        legacy: 'rgba(206, 239, 255, 0.5)',
        quartz: PRIMITIVE_COLORS.primary.aqua[100],
    },
    lightblue2: {
        legacy: '#3A9DCE',
        quartz: PRIMITIVE_COLORS.semantic.blue[500],
    },
    lighter: {
        legacy: '#E6E6E6',
        quartz: PRIMITIVE_COLORS.neutral.grey[100],
    },
    lightest: {
        legacy: '#F4F4F4',
        quartz: PRIMITIVE_COLORS.neutral.grey[50],
    },
    lightly: {
        legacy: '#CBCBCB',
        quartz: PRIMITIVE_COLORS.neutral.grey[200],
    },
    lightorange: {
        legacy: 'rgba(181, 65, 36, 0.1)',
        quartz: PRIMITIVE_COLORS.brand.orange[50],
    },
    lightpink: {
        legacy: '#FFA9B9',
        quartz: PRIMITIVE_COLORS.semantic.red[100],
    },
    lightpink2: {
        legacy: '#EFE3D9ED',
        quartz: PRIMITIVE_COLORS.secondary.tan[400],
    },
    lightpurple: {
        legacy: 'rgba(146, 94, 199, 0.1)',
        quartz: PRIMITIVE_COLORS.accent.purple[50],
    },
    lighttan: {
        legacy: '#FAF6F3',
        quartz: PRIMITIVE_COLORS.secondary.tan[100],
    },
    link: {
        legacy: '#3052DA',
        quartz: COLOR_TOKENS.text.link,
    },
    linkLight: {
        legacy: '#90B2FF',
        quartz: PRIMITIVE_COLORS.accent.cobalt[200],
    },
    linkPaychex: {
        legacy: '#0052CC',
        quartz: PRIMITIVE_COLORS.accent.cobalt[600],
    },
    literal000: {
        legacy: '#000',
        quartz: PRIMITIVE_COLORS.neutral.black,
    },
    literal000000: {
        legacy: '#000000',
        quartz: PRIMITIVE_COLORS.neutral.black,
    },
    literal0000FF: {
        legacy: '#0000FF',
        quartz: PRIMITIVE_COLORS.accent.cobalt[500],
    },
    literal003300: {
        legacy: '#003300',
        quartz: PRIMITIVE_COLORS.semantic.green[900],
    },
    literal004f93: {
        legacy: '#004f93',
        quartz: PRIMITIVE_COLORS.accent.cobalt[700],
    },
    literal0066CC: {
        legacy: '#0066CC',
        quartz: PRIMITIVE_COLORS.accent.cobalt[600],
    },
    literal0067d2: {
        legacy: '#0067d2',
        quartz: PRIMITIVE_COLORS.accent.cobalt[500],
    },
    literal007bff: {
        legacy: '#007bff',
        quartz: PRIMITIVE_COLORS.accent.cobalt[500],
    },
    literal008ab1: {
        legacy: '#008ab1',
        quartz: PRIMITIVE_COLORS.primary.aqua[800],
    },
    literal0098db: {
        legacy: '#0098db',
        quartz: PRIMITIVE_COLORS.primary.aqua[800],
    },
    literal00A885: {
        legacy: '#00A885',
        quartz: PRIMITIVE_COLORS.primary.aqua[900],
    },
    literal00b050: {
        legacy: '#00b050',
        quartz: PRIMITIVE_COLORS.semantic.green[500],
    },
    literal00f: {
        legacy: '#00f',
        quartz: PRIMITIVE_COLORS.accent.cobalt[500],
    },
    literal00FF00: {
        legacy: '#00FF00',
        quartz: PRIMITIVE_COLORS.semantic.green[500],
    },
    literal012345: {
        legacy: '#012345',
        quartz: PRIMITIVE_COLORS.accent.cobalt[900],
    },
    literal036fe3: {
        legacy: '#036fe3',
        quartz: PRIMITIVE_COLORS.accent.cobalt[500],
    },
    literal060: {
        legacy: '#060',
        quartz: PRIMITIVE_COLORS.semantic.green[800],
    },
    literal0A4C91: {
        legacy: '#0A4C91',
        quartz: PRIMITIVE_COLORS.accent.cobalt[700],
    },
    literal0C518A: {
        legacy: '#0C518A',
        quartz: PRIMITIVE_COLORS.primary.aqua[900],
    },
    literal0C60A1: {
        legacy: '#0C60A1',
        quartz: PRIMITIVE_COLORS.primary.aqua[900],
    },
    literal100C10: {
        legacy: '#100C10',
        quartz: PRIMITIVE_COLORS.neutral.black,
    },
    literal101414: {
        legacy: '#101414',
        quartz: PRIMITIVE_COLORS.neutral.black,
    },
    literal111: {
        legacy: '#111',
        quartz: PRIMITIVE_COLORS.neutral.black,
    },
    literal111111: {
        legacy: '#111111',
        quartz: PRIMITIVE_COLORS.neutral.black,
    },
    literal114e8f: {
        legacy: '#114e8f',
        quartz: PRIMITIVE_COLORS.accent.cobalt[700],
    },
    literal123: {
        legacy: '#123',
        quartz: PRIMITIVE_COLORS.semantic.green[900],
    },
    literal123456: {
        legacy: '#123456',
        quartz: PRIMITIVE_COLORS.accent.cobalt[900],
    },
    literal1402: {
        legacy: '#1402',
        quartz: rgba(PRIMITIVE_COLORS.semantic.green[900], 0.133),
    },
    literal150000: {
        legacy: '#150000',
        quartz: PRIMITIVE_COLORS.neutral.black,
    },
    literal15191d: {
        legacy: '#15191d',
        quartz: PRIMITIVE_COLORS.neutral.black,
    },
    literal1577c6: {
        legacy: '#1577c6',
        quartz: PRIMITIVE_COLORS.primary.aqua[800],
    },
    literal1577C6: {
        legacy: '#1577C6',
        quartz: PRIMITIVE_COLORS.primary.aqua[800],
    },
    literal17202A: {
        legacy: '#17202A',
        quartz: PRIMITIVE_COLORS.neutral.black,
    },
    literal1976d2: {
        legacy: '#1976d2',
        quartz: PRIMITIVE_COLORS.accent.cobalt[500],
    },
    literal1991eb: {
        legacy: '#1991eb',
        quartz: PRIMITIVE_COLORS.accent.cobalt[500],
    },
    literal1991EB: {
        legacy: '#1991EB',
        quartz: PRIMITIVE_COLORS.accent.cobalt[500],
    },
    literal1a91eb: {
        legacy: '#1a91eb',
        quartz: PRIMITIVE_COLORS.accent.cobalt[500],
    },
    literal1ABC9C: {
        legacy: '#1ABC9C',
        quartz: PRIMITIVE_COLORS.primary.aqua[800],
    },
    literal1B2A47: {
        legacy: '#1B2A47',
        quartz: PRIMITIVE_COLORS.semantic.blue[900],
    },
    literal200: {
        legacy: '#200',
        quartz: PRIMITIVE_COLORS.semantic.red[900],
    },
    literal222222: {
        legacy: '#222222',
        quartz: PRIMITIVE_COLORS.neutral.black,
    },
    literal223456: {
        legacy: '#223456',
        quartz: PRIMITIVE_COLORS.accent.cobalt[900],
    },
    literal234567: {
        legacy: '#234567',
        quartz: PRIMITIVE_COLORS.semantic.blue[900],
    },
    literal277ef6: {
        legacy: '#277ef6',
        quartz: PRIMITIVE_COLORS.accent.cobalt[500],
    },
    literal28282829: {
        legacy: '#28282829',
        quartz: rgba(PRIMITIVE_COLORS.neutral.black, 0.161),
    },
    literal28324E: {
        legacy: '#28324E',
        quartz: PRIMITIVE_COLORS.accent.purple[900],
    },
    literal287cd8: {
        legacy: '#287cd8',
        quartz: PRIMITIVE_COLORS.accent.cobalt[500],
    },
    literal2d2d2d: {
        legacy: '#2d2d2d',
        quartz: PRIMITIVE_COLORS.neutral.black,
    },
    literal2da0f8: {
        legacy: '#2da0f8',
        quartz: PRIMITIVE_COLORS.primary.aqua[700],
    },
    literal2da1f8: {
        legacy: '#2da1f8',
        quartz: PRIMITIVE_COLORS.primary.aqua[700],
    },
    literal2F6CD0: {
        legacy: '#2F6CD0',
        quartz: PRIMITIVE_COLORS.accent.cobalt[500],
    },
    literal3072cd: {
        legacy: '#3072cd',
        quartz: PRIMITIVE_COLORS.accent.cobalt[500],
    },
    literal323456: {
        legacy: '#323456',
        quartz: PRIMITIVE_COLORS.neutral.graphite[800],
    },
    literal32a852: {
        legacy: '#32a852',
        quartz: PRIMITIVE_COLORS.semantic.green[500],
    },
    literal333: {
        legacy: '#333',
        quartz: PRIMITIVE_COLORS.neutral.graphite[900],
    },
    literal333333: {
        legacy: '#333333',
        quartz: PRIMITIVE_COLORS.neutral.graphite[900],
    },
    literal345678: {
        legacy: '#345678',
        quartz: PRIMITIVE_COLORS.semantic.blue[800],
    },
    literal354052: {
        legacy: '#354052',
        quartz: PRIMITIVE_COLORS.neutral.graphite[800],
    },
    literal363535: {
        legacy: '#363535',
        quartz: PRIMITIVE_COLORS.neutral.graphite[900],
    },
    literal369: {
        legacy: '#369',
        quartz: PRIMITIVE_COLORS.primary.aqua[900],
    },
    literal373535: {
        legacy: '#373535',
        quartz: PRIMITIVE_COLORS.neutral.graphite[900],
    },
    literal373737: {
        legacy: '#373737',
        quartz: PRIMITIVE_COLORS.neutral.grey[900],
    },
    literal3a99ff: {
        legacy: '#3a99ff',
        quartz: PRIMITIVE_COLORS.accent.cobalt[400],
    },
    literal3D8EB9: {
        legacy: '#3D8EB9',
        quartz: PRIMITIVE_COLORS.primary.aqua[800],
    },
    literal404d58: {
        legacy: '#404d58',
        quartz: PRIMITIVE_COLORS.neutral.graphite[700],
    },
    literal404D58: {
        legacy: '#404D58',
        quartz: PRIMITIVE_COLORS.neutral.graphite[700],
    },
    literal41A85F: {
        legacy: '#41A85F',
        quartz: PRIMITIVE_COLORS.semantic.green[400],
    },
    literal423456: {
        legacy: '#423456',
        quartz: PRIMITIVE_COLORS.accent.purple[800],
    },
    literal439484: {
        legacy: '#439484',
        quartz: PRIMITIVE_COLORS.semantic.green[400],
    },
    literal444444: {
        legacy: '#444444',
        quartz: PRIMITIVE_COLORS.neutral.grey[900],
    },
    literal456: {
        legacy: '#456',
        quartz: PRIMITIVE_COLORS.neutral.graphite[700],
    },
    literal456789: {
        legacy: '#456789',
        quartz: PRIMITIVE_COLORS.semantic.blue[800],
    },
    literal475577: {
        legacy: '#475577',
        quartz: PRIMITIVE_COLORS.semantic.blue[800],
    },
    literal4a4a4a: {
        legacy: '#4a4a4a',
        quartz: PRIMITIVE_COLORS.neutral.grey[900],
    },
    literal4F4F4F: {
        legacy: '#4F4F4F',
        quartz: PRIMITIVE_COLORS.neutral.graphite[700],
    },
    literal515151: {
        legacy: '#515151',
        quartz: PRIMITIVE_COLORS.neutral.grey[800],
    },
    literal523456: {
        legacy: '#523456',
        quartz: PRIMITIVE_COLORS.accent.purple[800],
    },
    literal54ACD2: {
        legacy: '#54ACD2',
        quartz: PRIMITIVE_COLORS.primary.aqua[700],
    },
    literal553982: {
        legacy: '#553982',
        quartz: PRIMITIVE_COLORS.accent.purple[700],
    },
    literal555: {
        legacy: '#555',
        quartz: PRIMITIVE_COLORS.neutral.grey[800],
    },
    literal555555: {
        legacy: '#555555',
        quartz: PRIMITIVE_COLORS.neutral.grey[800],
    },
    literal58595b: {
        legacy: '#58595b',
        quartz: PRIMITIVE_COLORS.neutral.grey[800],
    },
    literal5A6478: {
        legacy: '#5A6478',
        quartz: PRIMITIVE_COLORS.neutral.graphite[500],
    },
    literal5B6C79: {
        legacy: '#5B6C79',
        quartz: COLOR_TOKENS.text.secondary,
    },
    literal61BD6D: {
        legacy: '#61BD6D',
        quartz: PRIMITIVE_COLORS.semantic.green[300],
    },
    literal623456: {
        legacy: '#623456',
        quartz: PRIMITIVE_COLORS.accent.purple[800],
    },
    literal626262: {
        legacy: '#626262',
        quartz: PRIMITIVE_COLORS.secondary.tan[900],
    },
    literal626878: {
        legacy: '#626878',
        quartz: PRIMITIVE_COLORS.neutral.graphite[600],
    },
    literal654321: {
        legacy: '#654321',
        quartz: PRIMITIVE_COLORS.semantic.yellow[900],
    },
    literal6EA9E4: {
        legacy: '#6EA9E4',
        quartz: PRIMITIVE_COLORS.semantic.blue[500],
    },
    literal673878: {
        legacy: '#673878',
        quartz: PRIMITIVE_COLORS.accent.purple[700],
    },
    literal676238: {
        legacy: '#676238',
        quartz: PRIMITIVE_COLORS.secondary.tan[900],
    },
    literal676843: {
        legacy: '#676843',
        quartz: PRIMITIVE_COLORS.secondary.tan[900],
    },
    literal676874: {
        legacy: '#676874',
        quartz: PRIMITIVE_COLORS.neutral.grey[700],
    },
    literal676878: {
        legacy: '#676878',
        quartz: PRIMITIVE_COLORS.neutral.grey[700],
    },
    literal6768EE: {
        legacy: '#6768EE',
        quartz: PRIMITIVE_COLORS.accent.cobalt[400],
    },
    literal6C8193: {
        legacy: '#6C8193',
        quartz: PRIMITIVE_COLORS.neutral.graphite[500],
    },
    literal6E7F8C: {
        legacy: '#6E7F8C',
        quartz: PRIMITIVE_COLORS.neutral.graphite[500],
    },
    literal6f4e37: {
        legacy: '#6f4e37',
        quartz: PRIMITIVE_COLORS.semantic.yellow[900],
    },
    literal700: {
        legacy: '#700',
        quartz: PRIMITIVE_COLORS.brand.orange[900],
    },
    literal717F91: {
        legacy: '#717F91',
        quartz: PRIMITIVE_COLORS.neutral.graphite[500],
    },
    literal721c24: {
        legacy: '#721c24',
        quartz: PRIMITIVE_COLORS.semantic.red[800],
    },
    literal723456: {
        legacy: '#723456',
        quartz: PRIMITIVE_COLORS.accent.purple[700],
    },
    literal7377a2: {
        legacy: '#7377a2',
        quartz: PRIMITIVE_COLORS.neutral.graphite[400],
    },
    literal75fffc: {
        legacy: '#75fffc',
        quartz: PRIMITIVE_COLORS.primary.aqua[500],
    },
    literal767676: {
        legacy: '#767676',
        quartz: PRIMITIVE_COLORS.neutral.grey[700],
    },
    literal777: {
        legacy: '#777',
        quartz: PRIMITIVE_COLORS.secondary.tan[800],
    },
    literal777777: {
        legacy: '#777777',
        quartz: PRIMITIVE_COLORS.secondary.tan[800],
    },
    literal785402: {
        legacy: '#785402',
        quartz: PRIMITIVE_COLORS.semantic.yellow[900],
    },
    literal794613: {
        legacy: '#794613',
        quartz: PRIMITIVE_COLORS.semantic.yellow[900],
    },
    literal7C706B: {
        legacy: '#7C706B',
        quartz: PRIMITIVE_COLORS.neutral.grey[700],
    },
    literal7ed321: {
        legacy: '#7ed321',
        quartz: PRIMITIVE_COLORS.semantic.yellow[700],
    },
    literal7f8fa4: {
        legacy: '#7f8fa4',
        quartz: PRIMITIVE_COLORS.neutral.graphite[400],
    },
    literal808080: {
        legacy: '#808080',
        quartz: PRIMITIVE_COLORS.neutral.grey[600],
    },
    literal823456: {
        legacy: '#823456',
        quartz: PRIMITIVE_COLORS.semantic.red[700],
    },
    literal852147: {
        legacy: '#852147',
        quartz: PRIMITIVE_COLORS.semantic.red[700],
    },
    literal858585: {
        legacy: '#858585',
        quartz: PRIMITIVE_COLORS.neutral.grey[600],
    },
    literal888: {
        legacy: '#888',
        quartz: PRIMITIVE_COLORS.neutral.grey[600],
    },
    literal899AA8: {
        legacy: '#899AA8',
        quartz: PRIMITIVE_COLORS.neutral.graphite[400],
    },
    literal8b91a5: {
        legacy: '#8b91a5',
        quartz: PRIMITIVE_COLORS.neutral.graphite[400],
    },
    literal900: {
        legacy: '#900',
        quartz: PRIMITIVE_COLORS.brand.orange[800],
    },
    literal91a4b7: {
        legacy: '#91a4b7',
        quartz: PRIMITIVE_COLORS.neutral.graphite[300],
    },
    literal9365B8: {
        legacy: '#9365B8',
        quartz: PRIMITIVE_COLORS.accent.purple[400],
    },
    literal963258: {
        legacy: '#963258',
        quartz: PRIMITIVE_COLORS.semantic.red[600],
    },
    literal97f295: {
        legacy: '#97f295',
        quartz: PRIMITIVE_COLORS.semantic.green[200],
    },
    literal987654: {
        legacy: '#987654',
        quartz: PRIMITIVE_COLORS.secondary.tan[800],
    },
    literal98a6b2: {
        legacy: '#98a6b2',
        quartz: PRIMITIVE_COLORS.neutral.graphite[300],
    },
    literal999: {
        legacy: '#999',
        quartz: PRIMITIVE_COLORS.neutral.grey[500],
    },
    literal9fe7f5: {
        legacy: '#9fe7f5',
        quartz: PRIMITIVE_COLORS.primary.aqua[500],
    },
    literalA2A2A2: {
        legacy: '#A2A2A2',
        quartz: PRIMITIVE_COLORS.secondary.tan[700],
    },
    literalA2E0FE: {
        legacy: '#A2E0FE',
        quartz: PRIMITIVE_COLORS.primary.aqua[500],
    },
    literalA38F84: {
        legacy: '#A38F84',
        quartz: PRIMITIVE_COLORS.neutral.grey[500],
    },
    literalA4E0FE: {
        legacy: '#A4E0FE',
        quartz: PRIMITIVE_COLORS.primary.aqua[500],
    },
    literalAA0000: {
        legacy: '#AA0000',
        quartz: PRIMITIVE_COLORS.brand.orange[800],
    },
    literalAABBCC: {
        legacy: '#AABBCC',
        quartz: PRIMITIVE_COLORS.neutral.graphite[200],
    },
    literalAbcdef: {
        legacy: '#abcdef',
        quartz: PRIMITIVE_COLORS.semantic.blue[300],
    },
    literalABCDEF: {
        legacy: '#ABCDEF',
        quartz: PRIMITIVE_COLORS.semantic.blue[300],
    },
    literalAdd: {
        legacy: '#add',
        quartz: PRIMITIVE_COLORS.semantic.green[100],
    },
    literalAFDDFD: {
        legacy: '#AFDDFD',
        quartz: PRIMITIVE_COLORS.primary.aqua[400],
    },
    literalAFDEFB: {
        legacy: '#AFDEFB',
        quartz: PRIMITIVE_COLORS.primary.aqua[400],
    },
    literalB1e5ff: {
        legacy: '#b1e5ff',
        quartz: PRIMITIVE_COLORS.primary.aqua[400],
    },
    literalB53349: {
        legacy: '#b53349',
        quartz: PRIMITIVE_COLORS.semantic.red[500],
    },
    literalB54124: {
        legacy: '#b54124',
        quartz: PRIMITIVE_COLORS.brand.orange[700],
    },
    literalB6354A: {
        legacy: '#B6354A',
        quartz: PRIMITIVE_COLORS.semantic.red[500],
    },
    literalB8312F: {
        legacy: '#B8312F',
        quartz: PRIMITIVE_COLORS.semantic.red[500],
    },
    literalB8C0C7: {
        legacy: '#B8C0C7',
        quartz: COLOR_TOKENS.border.subtle,
    },
    literalB8ebe5: {
        legacy: '#b8ebe5',
        quartz: PRIMITIVE_COLORS.semantic.blue[200],
    },
    literalBBDEFB: {
        legacy: '#BBDEFB',
        quartz: PRIMITIVE_COLORS.semantic.blue[200],
    },
    literalBcbec0: {
        legacy: '#bcbec0',
        quartz: PRIMITIVE_COLORS.neutral.grey[300],
    },
    literalBf9f85: {
        legacy: '#bf9f85',
        quartz: PRIMITIVE_COLORS.secondary.tan[700],
    },
    literalC1b6a4: {
        legacy: '#c1b6a4',
        quartz: PRIMITIVE_COLORS.neutral.grey[400],
    },
    literalC4ccd3: {
        legacy: '#c4ccd3',
        quartz: PRIMITIVE_COLORS.neutral.grey[200],
    },
    literalC7c7c7: {
        legacy: '#c7c7c7',
        quartz: PRIMITIVE_COLORS.neutral.grey[200],
    },
    literalCcc: {
        legacy: '#ccc',
        quartz: PRIMITIVE_COLORS.neutral.grey[200],
    },
    literalCCC: {
        legacy: '#CCC',
        quartz: PRIMITIVE_COLORS.neutral.grey[200],
    },
    literalCCCCCC: {
        legacy: '#CCCCCC',
        quartz: PRIMITIVE_COLORS.neutral.grey[200],
    },
    literalCE1C32: {
        legacy: '#CE1C32',
        quartz: PRIMITIVE_COLORS.semantic.red[500],
    },
    literalD14841: {
        legacy: '#D14841',
        quartz: PRIMITIVE_COLORS.brand.orange[600],
    },
    literalD1d1d1: {
        legacy: '#d1d1d1',
        quartz: PRIMITIVE_COLORS.neutral.grey[200],
    },
    literalD1D5D8: {
        legacy: '#D1D5D8',
        quartz: PRIMITIVE_COLORS.neutral.graphite[100],
    },
    literalD3d3d3: {
        legacy: '#d3d3d3',
        quartz: PRIMITIVE_COLORS.neutral.graphite[100],
    },
    literalD4d4d4: {
        legacy: '#d4d4d4',
        quartz: PRIMITIVE_COLORS.neutral.graphite[100],
    },
    literalD8D8D8: {
        legacy: '#D8D8D8',
        quartz: PRIMITIVE_COLORS.neutral.graphite[100],
    },
    literalD93232: {
        legacy: '#d93232',
        quartz: PRIMITIVE_COLORS.brand.orange[600],
    },
    literalDB6C4D: {
        legacy: '#DB6C4D',
        quartz: PRIMITIVE_COLORS.brand.orange[400],
    },
    literalDdd: {
        legacy: '#ddd',
        quartz: PRIMITIVE_COLORS.neutral.graphite[100],
    },
    literalDde1ef: {
        legacy: '#dde1ef',
        quartz: PRIMITIVE_COLORS.neutral.grey[100],
    },
    literalDDE1EF: {
        legacy: '#DDE1EF',
        quartz: PRIMITIVE_COLORS.neutral.grey[100],
    },
    literalDDEEFF: {
        legacy: '#DDEEFF',
        quartz: PRIMITIVE_COLORS.primary.aqua[100],
    },
    literalDEA3AD: {
        legacy: '#DEA3AD',
        quartz: PRIMITIVE_COLORS.semantic.red[200],
    },
    literalE0673C: {
        legacy: '#E0673C',
        quartz: PRIMITIVE_COLORS.brand.orange[500],
    },
    literalE0e0e0: {
        legacy: '#e0e0e0',
        quartz: PRIMITIVE_COLORS.neutral.grey[100],
    },
    literalE1e1e1: {
        legacy: '#e1e1e1',
        quartz: PRIMITIVE_COLORS.neutral.grey[100],
    },
    literalE1e5e9: {
        legacy: '#e1e5e9',
        quartz: PRIMITIVE_COLORS.neutral.grey[100],
    },
    literalE25041: {
        legacy: '#E25041',
        quartz: PRIMITIVE_COLORS.brand.orange[600],
    },
    literalE2E5EA: {
        legacy: '#E2E5EA',
        quartz: PRIMITIVE_COLORS.neutral.grey[100],
    },
    literalE3e36c: {
        legacy: '#e3e36c',
        quartz: PRIMITIVE_COLORS.semantic.yellow[300],
    },
    literalE62c14: {
        legacy: '#e62c14',
        quartz: PRIMITIVE_COLORS.brand.orange[600],
    },
    literalE62C14: {
        legacy: '#E62C14',
        quartz: PRIMITIVE_COLORS.brand.orange[600],
    },
    literalE6e6e6: {
        legacy: '#e6e6e6',
        quartz: PRIMITIVE_COLORS.neutral.grey[100],
    },
    literalE6e7e8: {
        legacy: '#e6e7e8',
        quartz: PRIMITIVE_COLORS.neutral.grey[100],
    },
    literalE6eaee: {
        legacy: '#e6eaee',
        quartz: PRIMITIVE_COLORS.neutral.grey[100],
    },
    literalE7eaee: {
        legacy: '#e7eaee',
        quartz: PRIMITIVE_COLORS.neutral.grey[100],
    },
    literalE7EAEE: {
        legacy: '#E7EAEE',
        quartz: PRIMITIVE_COLORS.neutral.grey[100],
    },
    literalE9e9e9: {
        legacy: '#e9e9e9',
        quartz: PRIMITIVE_COLORS.neutral.grey[100],
    },
    literalEaeaea: {
        legacy: '#eaeaea',
        quartz: PRIMITIVE_COLORS.neutral.grey[100],
    },
    literalEB6B56: {
        legacy: '#EB6B56',
        quartz: PRIMITIVE_COLORS.brand.orange[400],
    },
    literalEbebeb: {
        legacy: '#ebebeb',
        quartz: PRIMITIVE_COLORS.neutral.grey[100],
    },
    literalEDC772: {
        legacy: '#EDC772',
        quartz: PRIMITIVE_COLORS.semantic.yellow[400],
    },
    literalEee: {
        legacy: '#eee',
        quartz: PRIMITIVE_COLORS.semantic.green[50],
    },
    literalEEEEEE: {
        legacy: '#EEEEEE',
        quartz: PRIMITIVE_COLORS.semantic.green[50],
    },
    literalEFEFEF: {
        legacy: '#EFEFEF',
        quartz: PRIMITIVE_COLORS.neutral.graphite[50],
    },
    literalF0f1f3: {
        legacy: '#f0f1f3',
        quartz: PRIMITIVE_COLORS.neutral.graphite[50],
    },
    literalF15a26: {
        legacy: '#f15a26',
        quartz: PRIMITIVE_COLORS.brand.orange[500],
    },
    literalF1E3D8: {
        legacy: '#F1E3D8',
        quartz: PRIMITIVE_COLORS.secondary.tan[500],
    },
    literalF1f2f2: {
        legacy: '#f1f2f2',
        quartz: PRIMITIVE_COLORS.neutral.grey[50],
    },
    literalF26334: {
        legacy: '#F26334',
        quartz: PRIMITIVE_COLORS.brand.orange[500],
    },
    literalF2C661: {
        legacy: '#F2C661',
        quartz: PRIMITIVE_COLORS.semantic.yellow[400],
    },
    literalF2f2f2: {
        legacy: '#f2f2f2',
        quartz: PRIMITIVE_COLORS.neutral.grey[50],
    },
    literalF2f4f7: {
        legacy: '#f2f4f7',
        quartz: PRIMITIVE_COLORS.neutral.graphite[50],
    },
    literalF37934: {
        legacy: '#F37934',
        quartz: PRIMITIVE_COLORS.brand.orange[400],
    },
    literalF46433: {
        legacy: '#F46433',
        quartz: PRIMITIVE_COLORS.brand.orange[500],
    },
    literalF4B787: {
        legacy: '#F4B787',
        quartz: PRIMITIVE_COLORS.brand.orange[200],
    },
    literalF4f7f9: {
        legacy: '#f4f7f9',
        quartz: PRIMITIVE_COLORS.semantic.blue[50],
    },
    literalF57f17: {
        legacy: '#f57f17',
        quartz: PRIMITIVE_COLORS.brand.orange[500],
    },
    literalF57F17: {
        legacy: '#F57F17',
        quartz: PRIMITIVE_COLORS.brand.orange[500],
    },
    literalF5a623: {
        legacy: '#f5a623',
        quartz: PRIMITIVE_COLORS.semantic.yellow[600],
    },
    literalF5C144: {
        legacy: '#F5C144',
        quartz: PRIMITIVE_COLORS.semantic.yellow[500],
    },
    literalF5c6cb: {
        legacy: '#f5c6cb',
        quartz: PRIMITIVE_COLORS.semantic.red[100],
    },
    literalF5f5f5: {
        legacy: '#f5f5f5',
        quartz: PRIMITIVE_COLORS.secondary.tan[100],
    },
    literalF5f6f8: {
        legacy: '#f5f6f8',
        quartz: PRIMITIVE_COLORS.semantic.blue[50],
    },
    literalF5F8FA: {
        legacy: '#F5F8FA',
        quartz: PRIMITIVE_COLORS.semantic.blue[50],
    },
    literalF5FCFF: {
        legacy: '#F5FCFF',
        quartz: PRIMITIVE_COLORS.primary.aqua[50],
    },
    literalF5fdfe: {
        legacy: '#f5fdfe',
        quartz: PRIMITIVE_COLORS.primary.aqua[50],
    },
    literalF0F5FF: {
        legacy: '#F0F5FF',
        quartz: PRIMITIVE_COLORS.semantic.blue[50],
    },
    literalF6730C: {
        legacy: '#F6730C',
        quartz: PRIMITIVE_COLORS.brand.orange[500],
    },
    literalF79232: {
        legacy: '#f79232',
        quartz: PRIMITIVE_COLORS.semantic.yellow[600],
    },
    literalF7DA64: {
        legacy: '#F7DA64',
        quartz: PRIMITIVE_COLORS.semantic.yellow[400],
    },
    literalF7f6f1: {
        legacy: '#f7f6f1',
        quartz: PRIMITIVE_COLORS.secondary.tan[100],
    },
    literalF8d7da: {
        legacy: '#f8d7da',
        quartz: PRIMITIVE_COLORS.secondary.tan[500],
    },
    literalF8F9FA: {
        legacy: '#F8F9FA',
        quartz: PRIMITIVE_COLORS.neutral.graphite[50],
    },
    literalF90: {
        legacy: '#f90',
        quartz: PRIMITIVE_COLORS.semantic.yellow[600],
    },
    literalF9AB44: {
        legacy: '#F9AB44',
        quartz: PRIMITIVE_COLORS.semantic.yellow[500],
    },
    literalF9b579: {
        legacy: '#f9b579',
        quartz: PRIMITIVE_COLORS.semantic.yellow[300],
    },
    literalF9d88f: {
        legacy: '#f9d88f',
        quartz: PRIMITIVE_COLORS.semantic.yellow[300],
    },
    literalF9F6F3: {
        legacy: '#F9F6F3',
        quartz: PRIMITIVE_COLORS.secondary.tan[100],
    },
    literalF9FAFA: {
        legacy: '#F9FAFA',
        quartz: PRIMITIVE_COLORS.secondary.tan[50],
    },
    literalFAC447: {
        legacy: '#FAC447',
        quartz: PRIMITIVE_COLORS.semantic.yellow[500],
    },
    literalFAC51C: {
        legacy: '#FAC51C',
        quartz: PRIMITIVE_COLORS.semantic.yellow[500],
    },
    literalFaf9f6: {
        legacy: '#faf9f6',
        quartz: PRIMITIVE_COLORS.secondary.tan[100],
    },
    literalFafafa: {
        legacy: '#fafafa',
        quartz: PRIMITIVE_COLORS.secondary.tan[50],
    },
    literalFBA026: {
        legacy: '#FBA026',
        quartz: PRIMITIVE_COLORS.semantic.yellow[600],
    },
    literalFbfbfb: {
        legacy: '#fbfbfb',
        quartz: PRIMITIVE_COLORS.secondary.tan[50],
    },
    literalFCA809: {
        legacy: '#FCA809',
        quartz: PRIMITIVE_COLORS.semantic.yellow[600],
    },
    literalFce9e7: {
        legacy: '#fce9e7',
        quartz: PRIMITIVE_COLORS.brand.orange[50],
    },
    literalFdebdb: {
        legacy: '#fdebdb',
        quartz: PRIMITIVE_COLORS.secondary.tan[400],
    },
    literalFEB27F: {
        legacy: '#FEB27F',
        quartz: PRIMITIVE_COLORS.brand.orange[200],
    },
    literalFedcba: {
        legacy: '#fedcba',
        quartz: PRIMITIVE_COLORS.brand.orange[100],
    },
    literalFee: {
        legacy: '#fee',
        quartz: PRIMITIVE_COLORS.brand.orange[50],
    },
    literalFf0000: {
        legacy: '#ff0000',
        quartz: PRIMITIVE_COLORS.brand.orange[600],
    },
    literalFF0000: {
        legacy: '#FF0000',
        quartz: PRIMITIVE_COLORS.brand.orange[600],
    },
    literalFF1D1D: {
        legacy: '#FF1D1D',
        quartz: PRIMITIVE_COLORS.brand.orange[500],
    },
    literalFF33D7: {
        legacy: '#FF33D7',
        quartz: PRIMITIVE_COLORS.semantic.red[300],
    },
    literalFF4C00: {
        legacy: '#FF4C00',
        quartz: PRIMITIVE_COLORS.brand.orange[500],
    },
    literalFF5267: {
        legacy: '#FF5267',
        quartz: PRIMITIVE_COLORS.brand.orange[400],
    },
    literalFF5733: {
        legacy: '#FF5733',
        quartz: PRIMITIVE_COLORS.brand.orange[500],
    },
    literalFF6B00: {
        legacy: '#FF6B00',
        quartz: PRIMITIVE_COLORS.brand.orange[500],
    },
    literalFF7800: {
        legacy: '#FF7800',
        quartz: PRIMITIVE_COLORS.brand.orange[500],
    },
    literalFfb6ba: {
        legacy: '#ffb6ba',
        quartz: PRIMITIVE_COLORS.brand.orange[100],
    },
    literalFfeb3b: {
        legacy: '#ffeb3b',
        quartz: PRIMITIVE_COLORS.semantic.yellow[500],
    },
    literalFff: {
        legacy: '#fff',
        quartz: PRIMITIVE_COLORS.neutral.white,
    },
    literalFFF: {
        legacy: '#FFF',
        quartz: PRIMITIVE_COLORS.neutral.white,
    },
    literalFFFDFD: {
        legacy: '#FFFDFD',
        quartz: PRIMITIVE_COLORS.neutral.white,
    },
    literalFfffff: {
        legacy: '#ffffff',
        quartz: PRIMITIVE_COLORS.neutral.white,
    },
    literalRgb00040: {
        legacy: 'rgb(0 0 0 / 40%)',
        quartz: rgba(PRIMITIVE_COLORS.neutral.black, 0.4),
    },
    literalRgb130130130: {
        legacy: 'rgb(130 130 130)',
        quartz: PRIMITIVE_COLORS.neutral.grey[600],
    },
    literalRgb244244244: {
        legacy: 'rgb(244, 244, 244)',
        quartz: PRIMITIVE_COLORS.neutral.grey[50],
    },
    literalRgb24512723: {
        legacy: 'rgb(245, 127, 23)',
        quartz: PRIMITIVE_COLORS.brand.orange[500],
    },
    literalRgb245245245: {
        legacy: 'rgb(245, 245, 245)',
        quartz: PRIMITIVE_COLORS.secondary.tan[100],
    },
    literalRgb255255255: {
        legacy: 'rgb(255, 255, 255)',
        quartz: PRIMITIVE_COLORS.neutral.white,
    },
    literalRgb515151: {
        legacy: 'rgb(51, 51, 51)',
        quartz: PRIMITIVE_COLORS.neutral.graphite[900],
    },
    literalRgba0000: {
        legacy: 'rgba(0, 0, 0, 0)',
        quartz: rgba(PRIMITIVE_COLORS.neutral.black, 0),
    },
    literalRgba000004: {
        legacy: 'rgba(0, 0, 0, 0.04)',
        quartz: rgba(PRIMITIVE_COLORS.neutral.black, 0.04),
    },
    literalRgba000005: {
        legacy: 'rgba(0, 0, 0, 0.05)',
        quartz: rgba(PRIMITIVE_COLORS.neutral.black, 0.05),
    },
    literalRgba0000075: {
        legacy: 'rgba(0, 0, 0, 0.075)',
        quartz: rgba(PRIMITIVE_COLORS.neutral.black, 0.075),
    },
    literalRgba000008: {
        legacy: 'rgba(0, 0, 0, 0.08)',
        quartz: rgba(PRIMITIVE_COLORS.neutral.black, 0.08),
    },
    literalRgba00001: {
        legacy: 'rgba(0, 0, 0, 0.1)',
        quartz: rgba(PRIMITIVE_COLORS.neutral.black, 0.1),
    },
    literalRgba000012: {
        legacy: 'rgba(0, 0, 0, 0.12)',
        quartz: rgba(PRIMITIVE_COLORS.neutral.black, 0.12),
    },
    literalRgba0000124dd8cb: {
        legacy: 'rgba(0,0,0,0.12)',
        quartz: rgba(PRIMITIVE_COLORS.neutral.black, 0.12),
    },
    literalRgba000015: {
        legacy: 'rgba(0, 0, 0, 0.15)',
        quartz: rgba(PRIMITIVE_COLORS.neutral.black, 0.15),
    },
    literalRgba0000175: {
        legacy: 'rgba(0, 0, 0, 0.175)',
        quartz: rgba(PRIMITIVE_COLORS.neutral.black, 0.175),
    },
    literalRgba000024: {
        legacy: 'rgba(0, 0, 0, 0.24)',
        quartz: rgba(PRIMITIVE_COLORS.neutral.black, 0.24),
    },
    literalRgba000025: {
        legacy: 'rgba(0, 0, 0, 0.25)',
        quartz: rgba(PRIMITIVE_COLORS.neutral.black, 0.25),
    },
    literalRgba00003: {
        legacy: 'rgba(0, 0, 0, 0.3)',
        quartz: rgba(PRIMITIVE_COLORS.neutral.black, 0.3),
    },
    literalRgba00004: {
        legacy: 'rgba(0, 0, 0, 0.4)',
        quartz: rgba(PRIMITIVE_COLORS.neutral.black, 0.4),
    },
    literalRgba00005: {
        legacy: 'rgba(0, 0, 0, 0.5)',
        quartz: rgba(PRIMITIVE_COLORS.neutral.black, 0.5),
    },
    literalRgba00008: {
        legacy: 'rgba(0,0,0,0.8)',
        quartz: rgba(PRIMITIVE_COLORS.neutral.black, 0.8),
    },
    literalRgba0001: {
        legacy: 'rgba(0,0,0,.1)',
        quartz: rgba(PRIMITIVE_COLORS.neutral.black, 0.1),
    },
    literalRgba01522191: {
        legacy: 'rgba(0, 152, 219, 1)',
        quartz: PRIMITIVE_COLORS.primary.aqua[800],
    },
    literalRgba1271431641: {
        legacy: 'rgba(127, 143, 164, 1)',
        quartz: PRIMITIVE_COLORS.neutral.graphite[400],
    },
    literalRgba1661791901: {
        legacy: 'rgba(166, 179, 190, 1)',
        quartz: PRIMITIVE_COLORS.neutral.grey[400],
    },
    literalRgba181517301: {
        legacy: 'rgba(181, 51, 73, 0.1)',
        quartz: rgba(PRIMITIVE_COLORS.semantic.red[500], 0.1),
    },
    literalRgba22322322305: {
        legacy: 'rgba(223, 223, 223, 0.5)',
        quartz: rgba(PRIMITIVE_COLORS.neutral.grey[100], 0.5),
    },
    literalRgba2451272302: {
        legacy: 'rgba(245, 127, 23, 0.2)',
        quartz: rgba(PRIMITIVE_COLORS.brand.orange[500], 0.2),
    },
    literalRgba2451272305: {
        legacy: 'rgba(245, 127, 23, 0.5)',
        quartz: rgba(PRIMITIVE_COLORS.brand.orange[500], 0.5),
    },
    literalRgba2552552550: {
        legacy: 'rgba(255, 255, 255, 0)',
        quartz: rgba(PRIMITIVE_COLORS.neutral.white, 0),
    },
    literalRgba25525525507: {
        legacy: 'rgba(255, 255, 255, 0.7)',
        quartz: rgba(PRIMITIVE_COLORS.neutral.white, 0.7),
    },
    literalRgba404040018: {
        legacy: 'rgba(40, 40, 40, 0.18)',
        quartz: rgba(PRIMITIVE_COLORS.neutral.black, 0.18),
    },
    literalRgba404040032: {
        legacy: 'rgba(40, 40, 40, 0.32)',
        quartz: rgba(PRIMITIVE_COLORS.neutral.black, 0.32),
    },
    literalRgba404040036: {
        legacy: 'rgba(40, 40, 40, 0.36)',
        quartz: rgba(PRIMITIVE_COLORS.neutral.black, 0.36),
    },
    literalRgba51515104: {
        legacy: 'rgba(51, 51, 51, 0.4)',
        quartz: rgba(PRIMITIVE_COLORS.neutral.graphite[900], 0.4),
    },
    lockYellow: {
        legacy: '#F1C40F',
        quartz: PRIMITIVE_COLORS.semantic.yellow[500],
    },
    loginErrorBackground: {
        legacy: '#FCE9E7',
        quartz: PRIMITIVE_COLORS.semantic.red[50],
    },
    navyBlue: {
        legacy: '#0D1D38',
        quartz: PRIMITIVE_COLORS.accent.cobalt[900],
    },
    orange: {
        legacy: '#F15A26',
        quartz: PRIMITIVE_COLORS.brand.orange[500],
    },
    orange2: {
        legacy: '#F57C00',
        quartz: PRIMITIVE_COLORS.brand.orange[400],
    },
    orange3: {
        legacy: '#F57F17',
        quartz: PRIMITIVE_COLORS.brand.orange[400],
    },
    orange4: {
        legacy: '#FF5722',
        quartz: PRIMITIVE_COLORS.brand.orange[500],
    },
    orange5: {
        legacy: '#FFA96B',
        quartz: PRIMITIVE_COLORS.brand.orange[300],
    },
    orangeLight: {
        legacy: '#F1C257',
        quartz: PRIMITIVE_COLORS.semantic.yellow[500],
    },
    palePurple: {
        legacy: 'rgba(146, 94, 199, 0.4)',
        quartz: PRIMITIVE_COLORS.accent.purple[200],
    },
    peach: {
        legacy: '#FFAD76',
        quartz: PRIMITIVE_COLORS.brand.orange[300],
    },
    placeholder: {
        legacy: '#7E7E7E',
        quartz: PRIMITIVE_COLORS.neutral.grey[600],
    },
    prismGreen: {
        legacy: '#0E6207',
        quartz: PRIMITIVE_COLORS.semantic.green[700],
    },
    prismOrange: {
        legacy: '#EF6C00',
        quartz: PRIMITIVE_COLORS.brand.orange[400],
    },
    progressBar: {
        legacy: '#429EBD',
        quartz: PRIMITIVE_COLORS.primary.aqua[800],
    },
    progressBarBg: {
        legacy: '#E0E0E0',
        quartz: PRIMITIVE_COLORS.neutral.grey[100],
    },
    purple: {
        legacy: 'rgb(91, 79, 229)',
        quartz: PRIMITIVE_COLORS.accent.cobalt[400],
    },
    quartz400: {
        legacy: '#6FD9E2',
        quartz: PRIMITIVE_COLORS.primary.aqua[600],
    },
    ravenAzure: {
        legacy: '#1E95EE',
        quartz: PRIMITIVE_COLORS.semantic.blue[500],
    },
    ravenBlack: {
        legacy: '#000000',
        quartz: COLOR_TOKENS.text.primary,
    },
    ravenBlack2: {
        legacy: '#3a3a3a',
        quartz: PRIMITIVE_COLORS.neutral.grey[900],
    },
    ravenBlack3: {
        legacy: '#2B2B2B',
        quartz: PRIMITIVE_COLORS.neutral.black,
    },
    ravenDark: {
        legacy: '#2D2D2D',
        quartz: PRIMITIVE_COLORS.neutral.black,
    },
    ravenDarker: {
        legacy: '#1A1A1A',
        quartz: PRIMITIVE_COLORS.neutral.black,
    },
    ravenError: {
        legacy: '#E62C14',
        quartz: COLOR_TOKENS.text.error,
    },
    ravenFocus: {
        legacy: 'rgba(30, 149, 238, 0.5)',
        quartz: rgba(COLOR_TOKENS.text.selected, 0.5),
    },
    ravenGold: {
        legacy: '#DFBB0B',
        quartz: PRIMITIVE_COLORS.semantic.yellow[600],
    },
    ravenGrey: {
        legacy: '#797979',
        quartz: PRIMITIVE_COLORS.neutral.grey[700],
    },
    ravenGrey3: {
        legacy: '#C7C7C7',
        quartz: PRIMITIVE_COLORS.neutral.grey[300],
    },
    ravenGrey4: {
        legacy: '#EDEDED',
        quartz: PRIMITIVE_COLORS.neutral.grey[100],
    },
    ravenGrey5: {
        legacy: '#4D4D4D',
        quartz: PRIMITIVE_COLORS.neutral.grey[900],
    },
    ravenGrey6: {
        legacy: '#DCDCDC',
        quartz: PRIMITIVE_COLORS.neutral.grey[200],
    },
    ravenGrey8: {
        legacy: '#AEAEAE',
        quartz: PRIMITIVE_COLORS.neutral.grey[400],
    },
    ravenInput: {
        legacy: '#1E1E1E',
        quartz: PRIMITIVE_COLORS.neutral.black,
    },
    ravenInputDisabled: {
        legacy: '#E1E5E9',
        quartz: PRIMITIVE_COLORS.neutral.grey[100],
    },
    ravenLight: {
        legacy: '#7F8FA4',
        quartz: PRIMITIVE_COLORS.neutral.graphite[500],
    },
    ravenLight2: {
        legacy: '#A6B3BE',
        quartz: PRIMITIVE_COLORS.neutral.graphite[300],
    },
    ravenMuted: {
        legacy: '#EAEAEA',
        quartz: PRIMITIVE_COLORS.neutral.grey[100],
    },
    ravenOrchid: {
        legacy: '#B86DC9',
        quartz: PRIMITIVE_COLORS.accent.purple[500],
    },
    ravenPlaceholder: {
        legacy: '#9E9E9E',
        quartz: PRIMITIVE_COLORS.neutral.grey[500],
    },
    ravenPreAdded: {
        legacy: '#6BDFB8',
        quartz: PRIMITIVE_COLORS.semantic.green[300],
    },
    ravenPreDeleted: {
        legacy: '#FF8983',
        quartz: PRIMITIVE_COLORS.semantic.red[100],
    },
    ravenPrimary: {
        legacy: '#114E8F',
        quartz: COLOR_TOKENS.text.selected,
    },
    ravenSapphire: {
        legacy: '#1635AE',
        quartz: PRIMITIVE_COLORS.accent.cobalt[700],
    },
    ravenSecondary: {
        legacy: '#2DA1F8',
        quartz: PRIMITIVE_COLORS.semantic.blue[500],
    },
    ravenSoftBlue: {
        legacy: '#54B5FF',
        quartz: PRIMITIVE_COLORS.semantic.blue[400],
    },
    ravenSuccess: {
        legacy: '#2DA438',
        quartz: COLOR_TOKENS.text.success,
    },
    ravenToolbar: {
        legacy: '#F4F7F9',
        quartz: PRIMITIVE_COLORS.semantic.blue[50],
    },
    ravenWarning: {
        legacy: '#F57F17',
        quartz: COLOR_TOKENS.text.warning,
    },
    red: {
        legacy: '#B53349',
        quartz: COLOR_TOKENS.text.error,
    },
    red2: {
        legacy: '#E62C14',
        quartz: PRIMITIVE_COLORS.semantic.red[400],
    },
    red3: {
        legacy: '#DC3349',
        quartz: PRIMITIVE_COLORS.semantic.red[500],
    },
    red4: {
        legacy: '#B91C1C',
        quartz: PRIMITIVE_COLORS.semantic.red[500],
    },
    red5: {
        legacy: '#B81616',
        quartz: PRIMITIVE_COLORS.semantic.red[500],
    },
    red6: {
        legacy: '#E21B1B',
        quartz: PRIMITIVE_COLORS.semantic.red[400],
    },
    red7: {
        legacy: '#B22222',
        quartz: PRIMITIVE_COLORS.semantic.red[500],
    },
    semanticHighlight: {
        legacy: 'rgba(156, 222, 255, 0.5)',
        quartz: PRIMITIVE_COLORS.primary.aqua[200],
    },
    semanticLightGrey: {
        legacy: 'rgba(230, 230, 230, 0.25)',
        quartz: PRIMITIVE_COLORS.neutral.grey[50],
    },
    semanticLightOrange: {
        legacy: 'rgba(241, 90, 38, 0.15)',
        quartz: PRIMITIVE_COLORS.brand.orange[50],
    },
    semanticNewHighlight: {
        legacy: 'rgba(53, 166, 108, 0.6)',
        quartz: PRIMITIVE_COLORS.semantic.green[300],
    },
    semanticNoEdit: {
        legacy: 'rgba(250, 192, 59, 0.38)',
        quartz: PRIMITIVE_COLORS.semantic.yellow[100],
    },
    semanticRedError: {
        legacy: 'rgba(181, 51, 73, 0.36)',
        quartz: PRIMITIVE_COLORS.semantic.red[100],
    },
    semanticRedError10: {
        legacy: '#F8EBED',
        quartz: COLOR_TOKENS.background.error,
    },
    semanticRedError15: {
        legacy: 'rgba(181, 51, 73, 0.15)',
        quartz: PRIMITIVE_COLORS.semantic.red[50],
    },
    sodalite400: {
        legacy: '#5975E1',
        quartz: PRIMITIVE_COLORS.accent.cobalt[400],
    },
    steel: {
        legacy: '#647785',
        quartz: COLOR_TOKENS.text.secondary,
    },
    successGreen100: {
        legacy: '#BCD8CA',
        quartz: PRIMITIVE_COLORS.semantic.green[100],
    },
    successGreen50: {
        legacy: '#EAF2EE',
        quartz: COLOR_TOKENS.background.success,
    },
    successGreen500: {
        legacy: '#113623',
        quartz: PRIMITIVE_COLORS.semantic.green[900],
    },
    tabGrey: {
        legacy: '#F3F3F3',
        quartz: PRIMITIVE_COLORS.neutral.grey[50],
    },
    tableGrey: {
        legacy: '#FAFAFA',
        quartz: PRIMITIVE_COLORS.neutral.grey[50],
    },
    tableTitleRow: {
        legacy: '#F1F1F1',
        quartz: PRIMITIVE_COLORS.neutral.graphite[50],
    },
    tan: {
        legacy: '#EFE3D9',
        quartz: PRIMITIVE_COLORS.secondary.tan[500],
    },
    topaz200: {
        legacy: '#FFD9C0',
        quartz: PRIMITIVE_COLORS.brand.orange[100],
    },
    violet: {
        legacy: '#8159A5',
        quartz: PRIMITIVE_COLORS.accent.purple[500],
    },
    warningYellow100: {
        legacy: '#FFF3CD',
        quartz: PRIMITIVE_COLORS.semantic.yellow[50],
    },
    warningYellow500: {
        legacy: '#856404',
        quartz: PRIMITIVE_COLORS.semantic.yellow[800],
    },
    white: {
        legacy: '#FFFFFF',
        quartz: PRIMITIVE_COLORS.neutral.white,
    },
    white2: {
        legacy: '#F5FDFE',
        quartz: PRIMITIVE_COLORS.primary.aqua[50],
    },
    white3: {
        legacy: '#E6E2D5',
        quartz: PRIMITIVE_COLORS.secondary.tan[600],
    },
    white4: {
        legacy: '#F1F2F2',
        quartz: PRIMITIVE_COLORS.neutral.grey[50],
    },
    yellow: {
        legacy: '#FAC03B',
        quartz: COLOR_TOKENS.text.warning,
    },
    yellow2: {
        legacy: '#FFBF00',
        quartz: PRIMITIVE_COLORS.semantic.yellow[600],
    },
    yellowHighlight: {
        legacy: '#FFE966',
        quartz: PRIMITIVE_COLORS.semantic.yellow[400],
    },
});

export const isQuartzColorsEnabled = value =>
    value === true || ['true', '1', 'yes', 'y'].includes(String(value).toLowerCase());

export const getMigratedColors = flagValue => {
    const colorMode = isQuartzColorsEnabled(flagValue) ? 'quartz' : 'legacy';
    return deepFreeze(
        Object.keys(COLOR_MIGRATIONS).reduce((colors, key) => {
            colors[key] = COLOR_MIGRATIONS[key][colorMode];
            return colors;
        }, {})
    );
};

export const MIGRATED_COLORS = getMigratedColors(ENABLE_QUARTZ_COLORS);

export const LEGACY_COLOR_KEYS = deepFreeze({
    RED: 'red',
    LINK: 'link',
    LINK_LIGHT: 'linkLight',
    LINK_PAYCHEX: 'linkPaychex',
    BLUE: 'blue',
    BLUE_1: 'blue1',
    BLUE_2: 'blue2',
    BLUE_3: 'blue3',
    BLUE_4: 'blue4',
    BLUE_5: 'blue5',
    BLUE_HIGHLIGHT: 'blueHighlight',
    ORANGE: 'orange',
    FAVORITE_ORANGE: 'favoriteOrange',
    YELLOW: 'yellow',
    YELLOW_HIGHLIGHT: 'yellowHighlight',
    TAN: 'tan',
    BLACK_00: 'black00',
    BLACK: 'black',
    BLACK_2: 'black2',
    BLACK_3: 'black3',
    PEACH: 'peach',
    STEEL: 'steel',
    GREEN: 'green',
    GREEN_1: 'green1',
    GREEN_2: 'green2',
    GREEN_3: 'green3',
    SUCCESS_GREEN_50: 'successGreen50',
    PLACEHOLDER: 'placeholder',
    DARKEST: 'darkest',
    DARK: 'dark',
    LIGHT: 'light',
    LIGHT1: 'light1',
    LIGHTLY: 'lightly',
    LIGHTEST: 'lightest',
    LIGHTER: 'lighter',
    LIGHTBLUE: 'lightblue',
    LIGHTTAN: 'lighttan',
    LIGHTPINK: 'lightpink',
    LIGHTPINK_2: 'lightpink2',
    WHITE: 'white',
    WHITE_80: 'white',
    HANDBOOK_MULTISTATE: 'handbookMultistate',
    HANDBOOK_FEDERAL: 'handbookFederal',
    HANDBOOK_ONESTATE: 'handbookOnestate',
    SEMANTIC_HIGHLIGHT: 'semanticHighlight',
    SEMANTIC_NO_EDIT: 'semanticNoEdit',
    SEMANTIC_LIGHT_ORANGE: 'semanticLightOrange',
    SEMANTIC_RED_ERROR: 'semanticRedError',
    SEMANTIC_RED_ERROR15: 'semanticRedError15',
    SEMANTIC_RED_ERROR10: 'semanticRedError10',
    SEMANTIC_LIGHT_GREY: 'semanticLightGrey',
    SEMANTIC_NEW_HIGHLIGHT: 'semanticNewHighlight',
    BADGE_BLUE: 'badgeBlue',
    BADGE_BLUE25: 'badgeBlue25',
    BADGE_BLUE15: 'badgeBlue15',
    BADGE_BROWN: 'badgeBrown',
    BADGE_BROWN25: 'badgeBrown25',
    BADGE_GREEN: 'badgeGreen',
    BADGE_LIGHT_BLUE: 'badgeLightBlue',
    BADGE_ORANGE: 'badgeOrange',
    BADGE_ORANGE25: 'badgeOrange25',
    BADGE_ORANGE15: 'semanticLightOrange',
    BADGE_PINK: 'badgePink',
    BADGE_VIOLET: 'badgeViolet',
    BADGE_VIOLET25: 'badgeViolet25',
    BADGE_VIOLET15: 'badgeViolet15',
    BADGE_YELLOW: 'badgeYellow',
    BADGE_YELLOW25: 'badgeYellow25',
    BADGE_DARK_PINK: 'badgeDarkPink',
    BADGE_DARK_PINK50: 'badgeDarkPink50',
    BADGE_DARK_PINK25: 'badgeDarkPink25',
    BADGE_PRISM_GREEN25: 'badgePrismGreen25',
    BADGE_PRISM_ORANGE25: 'badgePrismOrange25',
    CATEGORY_BENEFITS: 'categoryBenefits',
    CATEGORY_DISCRIMINATION_EQUAL_EMPLOYMENT: 'categoryDiscriminationEqualEmployment',
    CATEGORY_LEAVES_ACCOMMODATIONS: 'categoryLeavesAccommodations',
    PALE_PURPLE: 'palePurple',
    CATEGORY_MANAGING_EMPLOYEES: 'categoryManagingEmployees',
    CATEGORY_WAGES_HOURS: 'categoryWagesHours',
    CATEGORY_TERMINATION: 'categoryTermination',
    CATEGORY_HIRING: 'categoryHiring',
    CATEGORY_SAFETY_HEALTH: 'categorySafetyHealth',
    TABLE_GREY: 'tableGrey',
    TABLE_TITLE_ROW: 'tableTitleRow',
    COMPONENTS_BOX_1: 'componentsBox1',
    COMPONENTS_BOX_2: 'componentsBox2',
    COMPONENTS_BLUE_15: 'componentsBlue15',
    COMPONENTS_DISABLED: 'componentsDisabled',
    COMPONENTS_TAN_30: 'componentsTan30',
    COMPONENTS_THE_GREY_STYLE: 'componentsTheGreyStyle',
    COMPONENTS_TAN_50: 'componentsTan50',
    COMPONENTS_TAN_2: 'componentsTan2',
    COMPONENTS_BLUE_50: 'semanticHighlight',
    COMPONENTS_THE_GREY_STYLE_50: 'componentsTheGreyStyle50',
    COMPONENTS_TABLE_TITLE_ROW: 'componentsTableTitleRow',
    COMPONENTS_BLUE_50_2: 'semanticHighlight',
    COMPONENTS_MODAL_OVERLAY_BLACK: 'componentsModalOverlayBlack',
    BRAND_RED_2: 'brandRed2',
    BRAND_BLUE_2: 'brandBlue2',
    BRAND_PEACH_2: 'brandPeach2',
    BRAND_YELLOW_2: 'brandYellow2',
    BRAND_GREEN_2: 'brandGreen2',
    BANNER_RED: 'bannerRed',
    BANNER_PEACH: 'bannerPeach',
    TAB_GREY: 'tabGrey',
    ACTIONS_TAN: 'actionsTan',
    GREY: 'grey',
    GREY_2: 'grey2',
    GREY_3: 'grey3',
    GREY_4: 'grey4',
    GREY_5: 'grey5',
    GREY_6: 'grey6',
    GREY_61: 'grey61',
    GREY_8: 'grey8',
    GREY_9: 'grey9',
    GREY_10: 'placeholder',
    GREY_11: 'grey11',
    GREY_12: 'grey12',
    GREY_13: 'grey13',
    GREY_16: 'grey16',
    GREY_17: 'grey17',
    GREY_18: 'grey18',
    GREY_19: 'grey19',
    GREY_20: 'grey20',
    GREY_21: 'grey21',
    GREY_22: 'grey22',
    GREY_D4: 'greyD4',
    GREY_23: 'grey23',
    GREY_24: 'grey22',
    GREY_25: 'light1',
    GREY_27: 'grey27',
    GREY_28: 'grey28',
    GREY_29: 'grey29',
    GREY_32: 'grey32',
    RED_2: 'red2',
    RED_3: 'red3',
    ALERT: 'alert',
    LOGIN_ERROR_BACKGROUND: 'loginErrorBackground',
    INPUT_ICON_ERROR: 'inputIconError',
    GREY_14: 'grey14',
    GREY_15: 'grey15',
    BROWN: 'brown',
    DARK_GREY: 'darkGrey',
    LIGHTPURPLE: 'lightpurple',
    LIGHTORANGE: 'lightorange',
    LIGHTBLUE1: 'lightblue1',
    LIGHTBLUE2: 'lightblue2',
    ORANGE_LIGHT: 'orangeLight',
    LIGHT_BANNER: 'lightBanner',
    NAVY_BLUE: 'navyBlue',
    GREY_PRISM_DESC: 'greyPrismDesc',
    PRISM_GREEN: 'prismGreen',
    PRISM_ORANGE: 'prismOrange',
    RED_4: 'red4',
    GREEN_4: 'green4',
    ORANGE_2: 'orange2',
    GREY_26: 'grey26',
    CHAT_BRAND_BASE: 'chatBrandBase',
    CHAT_AVATAR_WHITE: 'chatAvatarWhite',
    CHAT_AVATAR_BASE: 'chatAvatarBase',
    ORANGE_3: 'orange3',
    PROGRESS_BAR: 'progressBar',
    PROGRESS_BAR_BG: 'progressBarBg',
    RED_5: 'red5',
    RED_6: 'red6',
    BLUE_6: 'blue6',
    BLUE_7: 'blueHighlight',
    WHITE_2: 'white2',
    BLUE_8: 'blue8',
    BLUE_9: 'blue9',
    WHITE_3: 'white3',
    AQUA_BLUE: 'categoryDiscriminationEqualEmployment',
    VIOLET: 'violet',
    GREY_30: 'grey30',
    BRAND_YELLOW_3: 'brandYellow3',
    BRAND_YELLOW_4: 'brandYellow4',
    YELLOW_2: 'yellow2',
    LOCK_YELLOW: 'lockYellow',
    RED_7: 'red7',
    GREEN_5: 'green5',
    SUCCESS_GREEN_100: 'successGreen100',
    SUCCESS_GREEN_500: 'successGreen500',
    ERROR_RED_100: 'errorRed100',
    ERROR_RED_500: 'errorRed500',
    GREY_31: 'grey31',
    WHITE_4: 'white4',
    BRAND_BLUE_3: 'brandBlue3',
    WARNING_YELLOW_100: 'warningYellow100',
    WARNING_YELLOW_500: 'warningYellow500',
    INFORMATION_BLUE_50: 'brandBlue3',
    INFORMATION_BLUE_100: 'informationBlue100',
    INFORMATION_BLUE_200: 'informationBlue200',
    INFORMATION_BLUE_300: 'informationBlue300',
    INFORMATION_BLUE_500: 'informationBlue500',
    INFORMATION_BLUE_900: 'informationBlue900',
    ORANGE_4: 'orange4',
    SODALITE_400: 'sodalite400',
    QUARTZ_400: 'quartz400',
    ORANGE_5: 'orange5',
    TOPAZ_200: 'topaz200',
    PURPLE: 'purple',
});

export const getMigratedColor = legacyKey => MIGRATED_COLORS[LEGACY_COLOR_KEYS[legacyKey]];
